import { Injectable } from '@angular/core';
import { Rodeo } from '../model/rodeo';

@Injectable()
export class DateHelperService {
    public supportedTimeZones = [
        'America/New_York','America/Chicago','America/Denver','America/Los_Angeles', 'America/Honolulu',
        'Canada/Atlantic', 'Canada/Central', 'Canada/Eastern', 'Canada/Mountain', 'Canada/Newfoundland', 'Canada/Pacific', 'Canada/Saskatchewan', 'Canada/Yukon'
    ];

    public rodeoDateFields = [
        'registrationStartDate',
        'registrationEndDate',
        'lateRegistrationStartDate',
        'tradeDeadline',
        'emergencyTradeDeadline',
        'paymentPlanDueBy',
        'paymentPlanEnrollmentStartDate',
        'paymentPlanEnrollmentEndDate',
        'checksDueBy'
    ];

    constructor() { }

    public formatDateToSelectedTimeZone(dateString: string, timeZone: string): string | null {
        if (!timeZone) { return dateString; }

        const date = new Date(dateString);
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) { return null; }

        let options: Intl.DateTimeFormatOptions = {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false,
            timeZone: timeZone
        }

        const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

        let datePartsToFormat = formattedDate.split(',');
        const datePart = datePartsToFormat[0]; // '02/25/2025'
        const timePart = datePartsToFormat[1].trim().split(' ')[0]; // ' 10:00:00 PM '

        const dateparts = datePart.split('/'); // [02, 25, 2025]

        let month = dateparts[0];
        if (+month < 10) {
            month = `0${month}`;
        }
        let day = dateparts[1];
        if (+day < 10) {
            day = `0${day}`;
        }
        let year = dateparts[2];
        if (+year < 10) {
            year = `0${year}`;
        }

        const formattedDateParts = `${year}-${month}-${day}T${timePart}`;
        return formattedDateParts;
    }

    // Only change timezone offset without altering the time values and convert to UTC
    // Convert a date into the selected timezone and then into an ISO string for saving
    public convertDateToUTC(dateToUpdate: string, timeZoneName: string): string {
        if (!dateToUpdate || dateToUpdate === '') return dateToUpdate;

        // Check if dateToUpdate is a valid date
        // Example: '0001-01-01 ...' for C# storing 'null' as minDateValue (EmergencyTradeDeadline, TurnoutGraceDeadline)
        let date = new Date(dateToUpdate);
        if (isNaN(date.getTime())) {
            // Create a new date if the input is invalid
            dateToUpdate = new Date().toLocaleString();
        }

        if (dateToUpdate.includes('Z')) {
            dateToUpdate = this.stripTimezone(dateToUpdate);
        }

        const utcOffsetString = this.getUtcOffsetString(dateToUpdate, timeZoneName);
        const formattedDate = dateToUpdate + utcOffsetString;
        const isoString = new Date(formattedDate).toISOString();        
        return isoString;
    }

    public getUtcOffsetString(dateString: string, timeZone: string): string {
        const date = new Date(dateString);
        if (!dateString || !(date instanceof Date) || isNaN(date.getTime())) { return ''; }

        if (!timeZone) {
            return '';
        }

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            timeZoneName: 'longOffset'
        });

        const formattedDate = formatter.format(new Date(dateString));

        const offsetString = formattedDate.slice(-6);
        return offsetString;
    }

    public mapToSupportedTimeZone(timeZone: string): string {
        if (this.supportedTimeZones.includes(timeZone)) {
            return timeZone;
        }
      
        // Default fallback if no match found (default to Central Time)
        return 'America/Chicago';
    }

    public getTimeZoneAbbreviation(dateString: string, rodeo?: Rodeo): string {
        const date = new Date(dateString); // converts to local based on time zone offset value
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) { return null; }

        let timeZone = rodeo ? rodeo.rodeoTimezone : null;

        if (!timeZone) {
          return ''; // Return empty if no timezone can be determined
        }

        const formattedTime = date.toLocaleTimeString('en-US', {
            timeZone: timeZone,
            timeZoneName: 'short'
        });
    
        // Extract abbreviation (e.g., PST, PDT, CST)
        return formattedTime.slice(-3);
    }

    public stripTimezone(date: string) {
        if (!date)
            return date;

        return new Date(new Date(date).getTime() + (new Date(date).getTimezoneOffset() * -60 * 1000)).toISOString().substring(0, '2011-10-05T14:48'.length);
    }
}



// WEBPACK FOOTER //
// ./src/services/date-helper.ts